import { useState } from 'react';
import { Flag } from 'lucide-react';
import { avgWeightOn } from '../logic/measurements';
import { FONT_MONO } from '../theme';
import { Button, Card, Input } from '../ui';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const fmtDay = k => { const [, m, d] = k.split('-').map(Number); return `${d} ${MONTHS[m - 1]}`; };
const kg = v => `${v > 0 ? '+' : v < 0 ? '−' : ''}${Math.abs(v).toFixed(2)}`;
const fmtKcal = v => Math.round(v).toLocaleString('en-GB');

const STATUS = {
  'on-track': { label: 'ON TRACK', tone: 'good' },
  close: { label: 'CLOSE', tone: 'warn' },
  behind: { label: 'BEHIND', tone: 'bad' },
  reached: { label: 'REACHED', tone: 'good' },
  past: { label: 'DATE PASSED', tone: 'warn' },
  'no-trend': { label: 'NEED DATA', tone: 'muted' },
  'no-data': { label: 'NEED DATA', tone: 'muted' },
};
const toneColor = (t, tone) => (tone === 'good' ? t.accent : tone === 'warn' ? '#f59e0b' : tone === 'bad' ? t.danger : t.textMuted);

/* Profile: set or clear the target weight and date. The start is stamped
 * when the goal is set, so the progress bar has a fixed beginning. */
export function WeightGoalEditor({ t, state, setState, today }) {
  const goal = state.profile?.weightGoal || null;
  const [weight, setWeight] = useState(goal ? String(goal.weight) : '');
  const [date, setDate] = useState(goal ? goal.date : '');
  const w = parseFloat(String(weight).replace(',', '.'));
  const valid = Number.isFinite(w) && w >= 30 && w <= 300 && /^\d{4}-\d{2}-\d{2}$/.test(date) && date > today;
  const changed = !goal || goal.weight !== w || goal.date !== date;

  const save = () => setState(prev => {
    const old = prev.profile?.weightGoal;
    const current = avgWeightOn(prev.bodyMetrics, today);
    // A new target restarts the progress bar; moving only the date keeps it.
    const keepStart = old && old.weight === w && old.startWeight;
    const next = {
      weight: w, date,
      startWeight: keepStart ? old.startWeight : (current ?? old?.startWeight ?? null),
      startDate: keepStart ? old.startDate : today,
    };
    if (next.startWeight == null) delete next.startWeight;
    return { ...prev, profile: { ...prev.profile, weightGoal: next } };
  });
  const clear = () => {
    setState(prev => {
      const { weightGoal, ...profile } = prev.profile || {};
      return { ...prev, profile };
    });
    setWeight(''); setDate('');
  };

  return (
    <Card t={t} padding={20}>
      <div style={{ fontSize: 14, fontWeight: 700, color: t.text, marginBottom: 4 }}>Weight goal</div>
      <div style={{ fontSize: 12, color: t.textMuted, marginBottom: 12 }}>
        A target weight and date. Stats and Meals then show the pace you need against your trend.
      </div>
      {/* minmax(0, 1fr): a date input won't shrink below its own width otherwise and widens the page. */}
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: 12 }}>
        <Input t={t} inputMode="decimal" aria-label="Target weight in kg" placeholder="Target kg, e.g. 86"
          value={weight} onChange={e => setWeight(e.target.value)} style={{ minWidth: 0, width: '100%' }} />
        <Input t={t} type="date" aria-label="Target date" value={date} min={today} onChange={e => setDate(e.target.value)}
          style={{ minWidth: 0, width: '100%' }} />
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
        <Button t={t} variant="primary" size="sm" disabled={!valid || !changed} onClick={save} style={{ minHeight: 40 }}>
          {goal ? 'Update goal' : 'Set goal'}
        </Button>
        {goal && <Button t={t} size="sm" variant="ghost" onClick={clear} style={{ minHeight: 40 }}>Clear</Button>}
      </div>
    </Card>
  );
}

function advice(p) {
  if (p.status === 'reached') return 'Goal reached. Set a new one, or switch the profile goal to maintenance.';
  if (p.status === 'past') return 'The date has passed. Set a new date to keep tracking.';
  if (p.status === 'no-data') return 'Log your weight to start tracking.';
  if (p.status === 'no-trend') return 'Weigh in a few times a week: the trend needs about 10 days of weigh-ins.';
  if (p.status === 'on-track') return p.kcalChange > 50 ? 'Ahead of the pace you need.' : 'Right on the pace you need.';
  return `About ${Math.abs(p.kcalChange)} kcal a day ${p.kcalChange < 0 ? 'less' : 'more'} closes the gap.`;
}

/* Stats: where the goal stands. p = logic/goal weightGoalProgress. */
export function GoalProgressCard({ t, p, calorieTarget }) {
  if (!p) return null;
  const st = STATUS[p.status];
  const color = toneColor(t, st.tone);
  const verb = p.direction === 'lose' ? 'Cut' : 'Gain';
  const showPace = p.neededPerWeek !== null;
  return (
    <Card t={t} padding={20}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <Flag size={15} color={color} />
        <span style={{ flex: 1, fontSize: 13, fontWeight: 700, color: t.text }}>{verb} to {p.target} kg by {fmtDay(p.date)}</span>
        <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.06em', color, padding: '3px 8px', borderRadius: 999, border: `1px solid ${color}` }}>
          {st.label}
        </span>
      </div>
      {p.progress !== null && (
        <div style={{ marginBottom: 10 }}>
          <div style={{ height: 8, borderRadius: 999, background: t.surfaceAlt, overflow: 'hidden' }}>
            <div style={{ width: `${Math.round(p.progress * 100)}%`, height: '100%', background: color, transition: 'width 0.3s' }} />
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: t.textFaint, fontFamily: FONT_MONO, marginTop: 4 }}>
            <span>{p.startWeight} kg{p.startDate ? ` · ${fmtDay(p.startDate)}` : ''}</span>
            <span>{Math.round(p.progress * 100)}%</span>
            <span>{p.target} kg</span>
          </div>
        </div>
      )}
      {p.current !== null && (
        <div style={{ fontSize: 13, color: t.text, marginBottom: 8 }}>
          <b style={{ fontFamily: FONT_MONO }}>{p.current.toFixed(1)} kg</b> now (7-day avg)
          {p.toGo !== null && p.status !== 'reached' ? ` · ${Math.abs(p.toGo).toFixed(1)} kg to go` : ''}
          {p.daysLeft > 0 ? ` · ${p.weeksLeft} weeks left` : ''}
        </div>
      )}
      {showPace && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
          {[['Needed', p.neededPerWeek], ['Your trend', p.trendPerWeek]].map(([label, v]) => (
            <div key={label} style={{ padding: '8px 10px', borderRadius: 10, background: t.surfaceAlt, border: `1px solid ${t.cardBorder}` }}>
              <div style={{ fontSize: 10, color: t.textFaint, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</div>
              <div style={{ fontSize: 16, fontFamily: FONT_MONO, fontWeight: 700, color: label === 'Your trend' ? color : t.text }}>
                {v === null ? '—' : `${kg(v)}`}<span style={{ fontSize: 11, fontWeight: 400, color: t.textMuted }}> kg/wk</span>
              </div>
            </div>
          ))}
        </div>
      )}
      {p.projectedOnDate !== null && p.status !== 'reached' && (
        <div style={{ fontSize: 12, color: t.textMuted, marginBottom: 6 }}>
          At this pace: {p.projectedOnDate.toFixed(1)} kg on {fmtDay(p.date)}
          {p.projectedDate ? ` · ${p.target} kg around ${fmtDay(p.projectedDate)}` : ''}.
        </div>
      )}
      <div style={{ fontSize: 12, color: t.text, lineHeight: 1.45 }}>
        {advice(p)}
        {(p.status === 'behind' || p.status === 'close') && p.suggestedKcal !== null && (
          <> Roughly <b>{fmtKcal(p.suggestedKcal)} kcal/day</b>{calorieTarget ? ` (target now ${fmtKcal(calorieTarget)})` : ''}.</>
        )}
      </div>
      {p.aggressive && (
        <div style={{ fontSize: 12, color: '#f59e0b', marginTop: 6 }}>
          That pace is over 1% of your bodyweight a week, which tends to cost muscle. A later date is safer.
        </div>
      )}
    </Card>
  );
}

/* Meals, under the weight input: the goal in one line. */
export function GoalLine({ t, p }) {
  if (!p || p.neededPerWeek === null) return null;
  const st = STATUS[p.status];
  return (
    <div style={{ fontSize: 11, color: t.textMuted, marginTop: 8, fontFamily: FONT_MONO }}>
      Goal {p.target} kg · {fmtDay(p.date)}: need {kg(p.neededPerWeek)}/wk
      {p.trendPerWeek !== null ? `, trend ${kg(p.trendPerWeek)}` : ''}
      <span style={{ color: toneColor(t, st.tone), fontWeight: 700 }}> · {st.label.toLowerCase()}</span>
    </div>
  );
}
